import { useState, useMemo, useEffect, useCallback } from "react";
import type { NotifiableError } from "@bugsnag/js";
import { bugsnagClient } from "bugsnag";
import api from "utils/api";
import { getPlanAlignmentFromDocumentation } from "./utils";
import { PLAN_ALIGNMENT_DEFAULT_VALUES } from "./constants";
import type { PlanAlignment } from "utils/math/applyPlanAlignmentToPoint/types";
import type { UseEditPlanAlignmentState } from "./types";

export const useEditPlanAlignmentState: UseEditPlanAlignmentState = ({
  documentationId,
  documentationPlanAlignment,
  refetchDocumentation,
}) => {
  const [isEditingAlignment, setIsEditingAlignment] = useState(false);
  const [isUpdatingAlignment, setIsUpdatingAlignment] = useState(false);

  const [alignmentValues, setAlignmentValues] = useState<PlanAlignment>(
    PLAN_ALIGNMENT_DEFAULT_VALUES
  );

  const resetAlignmentValues = useCallback(() => {
    setAlignmentValues(
      getPlanAlignmentFromDocumentation({ documentationPlanAlignment })
    );
  }, [documentationPlanAlignment]);

  const floorPlanAlignment = useMemo(
    () =>
      isEditingAlignment
        ? alignmentValues
        : getPlanAlignmentFromDocumentation({ documentationPlanAlignment }),
    [isEditingAlignment, alignmentValues, documentationPlanAlignment]
  );

  const startEditingAlignment = useCallback(() => {
    setIsEditingAlignment(true);
    resetAlignmentValues();
  }, [resetAlignmentValues]);

  const cancelEditingAlignment = useCallback(() => {
    setIsEditingAlignment(false);
    resetAlignmentValues();
  }, [resetAlignmentValues]);

  const updatePlanAlignment = useCallback(async () => {
    if (!documentationId) return;

    setIsUpdatingAlignment(true);
    try {
      await api.updateDocumentation(documentationId, {
        planAlignment: alignmentValues,
      });
      setIsEditingAlignment(false);
      await refetchDocumentation();
    } catch (error) {
      bugsnagClient.notify(error as NotifiableError);
      console.error("Error updating plan alignment:", error);
    } finally {
      setIsUpdatingAlignment(false);
    }
  }, [documentationId, alignmentValues, refetchDocumentation]);

  useEffect(() => {
    if (!isEditingAlignment) resetAlignmentValues();
  }, [isEditingAlignment, resetAlignmentValues]);

  return {
    isEditingAlignment,
    isUpdatingAlignment,
    alignmentValues,
    setAlignmentValues, // for sliders/inputs
    floorPlanAlignment, // what renderer should use
    startEditingAlignment,
    cancelEditingAlignment,
    updatePlanAlignment,
  };
};
